// hooks/useNotifications.ts
//
// One hook that owns:
//   * Initial fetch via /api/notifications (server-side route → RLS scoped SELECT)
//   * Realtime postgres_changes subscription scoped to user_id = auth.uid()
//     (RLS still scopes the payload server-side; client filter is for efficiency)
//   * Mark-read + mark-all-read mutations via /api/notifications/mark-*
//   * Splits into unread/read lists, exposes unreadCount
//
// Mutations apply optimistic updates: mark-read flips is_read=true in the
// React Query cache immediately (cancel-in-flight, snapshot, optimistically
// update; rollback in onError; invalidate in onSettled). This avoids a
// loading flash when the user clicks the bell — the count drops instantly
// and the server confirms in the background.
//
// On WebSocket reconnect, the React Query client refetches the initial
// list automatically (refetchOnReconnect: true), so missed events are
// reconciled without manual lastEventId tracking.

'use client'

import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth/AuthProvider'
import { subscribeToNotificationInvalidations } from '@/lib/supabase/browserRealtime'
import { type Notification, asNotification, type NotificationRow } from '@/lib/types/notifications'

const QUERY_KEY = ['notifications'] as const

async function fetchNotifications(): Promise<Notification[]> {
  const res = await fetch('/api/notifications?limit=100', { cache: 'no-store' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const json = await res.json()
  return (json.notifications ?? []) as Notification[]
}

export function useNotifications() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchNotifications,
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  })

  // Realtime subscription
  //
  // Channel-lifecycle hardening (29 May 2026):
  //   @supabase/realtime-js keeps a registry of channels keyed by topic.
  //   If `supabase.channel(topic)` is called for a topic that already
  //   exists in the registry, it returns the EXISTING channel object —
  //   and `.on('postgres_changes', ...)` on a channel where `.subscribe()`
  //   was already fired throws:
  //       "cannot add `postgres_changes` callbacks ... after `subscribe()`."
  //
  //   `removeChannel()` is asynchronous (sends UNSUBSCRIBE over the WS and
  //   only fully removes the channel after the server ACK, ~hundreds of
  //   ms). When this hook unmounts → remounts faster than that round-trip
  //   (auth state churn, React 18 Suspense, realtime tenant restart →
  //   forced reconnect), the new mount hits the stale channel and bombs.
  //
  //   Two fixes layered together:
  //     1. UNIQUE channel name per mount via crypto.randomUUID() —
  //        guarantees a fresh registry slot, so even if a previous
  //        channel is still in the "closing" state it cannot collide.
  //     2. Defensive sweep of any leftover channel matching this user's
  //        notification topic prefix before subscribing — belt-and-braces
  //        for hot-reload / Fast Refresh cases.
  useEffect(() => {
    if (!user?.id) return

    return subscribeToNotificationInvalidations({
      userId: user.id,
      nonce: crypto.randomUUID(),
      onInsert: (payload) => {
        const row = payload.new as NotificationRow
        queryClient.setQueryData<Notification[]>(QUERY_KEY, (prev) => {
          const existing = prev ?? []
          if (existing.some((notification) => notification.id === row.id)) return existing
          return [asNotification(row), ...existing]
        })
      },
      onUpdate: (payload) => {
        const row = payload.new as NotificationRow
        queryClient.setQueryData<Notification[]>(QUERY_KEY, (prev) => {
          if (!prev) return prev
          return prev.map((notification) =>
            notification.id === row.id ? asNotification(row) : notification
          )
        })
      }
    })
  }, [user?.id, queryClient])

  const markRead = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch('/api/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()).updated as number
    },
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const previous = queryClient.getQueryData<Notification[]>(QUERY_KEY)
      const idSet = new Set(ids)
      queryClient.setQueryData<Notification[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((n) => (idSet.has(n.id) ? { ...n, is_read: true } : n))
      )
      return { previous }
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(QUERY_KEY, ctx.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  })

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/notifications/mark-all-read', { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()).updated as number
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const previous = queryClient.getQueryData<Notification[]>(QUERY_KEY)
      queryClient.setQueryData<Notification[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((n) => ({ ...n, is_read: true }))
      )
      return { previous }
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(QUERY_KEY, ctx.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  })

  const all = query.data ?? []
  const unread = all.filter((n) => !n.is_read)
  const read = all.filter((n) => n.is_read)

  return {
    all,
    unread,
    read,
    unreadCount: unread.length,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    markRead: (ids: string[]) => markRead.mutateAsync(ids),
    markAllRead: () => markAllRead.mutateAsync(),
    markReadPending: markRead.isPending,
    markAllReadPending: markAllRead.isPending
  }
}
